import { useState } from 'react';
import { ChevronDown, ChevronUp, Link2, Music, Trash2, Youtube } from 'lucide-react';
import type { PlanItem } from '@app/types';
import { Select, Textarea, cx } from '@app/components/ui';
import { MUSICAL_KEYS } from './constants';

interface SetlistItemProps {
  item: PlanItem;
  idx: number;
  isYouTube: boolean;
  onUpdateKey: (itemId: string, key: string) => void;
  onRemove: (itemId: string) => void;
  onUpdateSection: (itemId: string, section: string) => void;
  onUpdateNotes: (itemId: string, notes: string) => void;
  onUpdateLyrics: (itemId: string, lyrics: string) => void;
  sections: string[];
}

/**
 * One setlist row: number, title, section and key, and an expandable notes and
 * lyrics editor. On a phone the section and key wrap onto their own line, so
 * there is one set of controls at every width.
 */
export const SetlistItem: React.FC<SetlistItemProps> = ({ item, idx, isYouTube, onUpdateKey, onRemove, onUpdateSection, onUpdateNotes, onUpdateLyrics, sections }) => {
  const [expanded, setExpanded] = useState(false);
  const isSong = item.type === 'Song';

  return (
    <div className={cx('rounded-[14px] border', expanded ? 'border-line2 bg-card' : 'border-transparent bg-fill hover:bg-fill2')} style={{ transition: 'background .15s ease' }}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-2.5 py-2">
        <span className="font-mono text-[12px] font-bold text-t4 w-5 text-right shrink-0">{idx + 1}</span>
        <span className="shrink-0 text-t4" aria-hidden="true">
          {isSong ? isYouTube ? <Youtube size={14} className="text-acc" /> : <Music size={14} /> : <span className="w-2 h-2 rounded-full bg-t5 inline-block" />}
        </span>
        <div className="flex-1 min-w-[120px]">
          <p className="text-[13.5px] font-semibold truncate">{item.title}</p>
          {!isSong && <p className="text-[11.5px] text-t4">{item.type}</p>}
          {item.notes && !expanded && <p className="text-[12px] text-t4 truncate">{item.notes}</p>}
        </div>
        {item.youtubeUrl && (
          <a
            href={item.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 rounded-full text-t4 hover:text-acc hover:bg-acc-bg flex items-center justify-center shrink-0"
            title="Open YouTube link"
            aria-label={`Open ${item.title} on YouTube`}
          >
            <Link2 size={13} aria-hidden="true" />
          </a>
        )}
        {(sections.length > 0 || isSong) && (
          <div className="flex items-center gap-1.5 order-last basis-full pl-7 sm:order-none sm:basis-auto sm:pl-0">
            {sections.length > 0 && (
              <div className="w-[140px] sm:w-[130px]">
                <Select
                  size="sm"
                  aria-label={`Section for ${item.title}`}
                  value={item.section || ''}
                  onChange={(v) => onUpdateSection(item.id, v)}
                  options={[{ value: '', label: 'No section' }, ...sections.map((s) => ({ value: s, label: s }))]}
                />
              </div>
            )}
            {isSong && (
              <div className="w-[76px]">
                <Select
                  size="sm"
                  aria-label={`Key for ${item.title}`}
                  value={item.key || ''}
                  onChange={(v) => onUpdateKey(item.id, v)}
                  options={[{ value: '', label: 'Key' }, ...MUSICAL_KEYS.map((k) => ({ value: k, label: k }))]}
                />
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label={expanded ? `Close notes for ${item.title}` : `Notes and lyrics for ${item.title}`}
          title={expanded ? 'Collapse' : 'Edit notes & lyrics'}
          className="w-7 h-7 rounded-full text-t4 hover:text-t1 hover:bg-fill2 flex items-center justify-center shrink-0"
        >
          {expanded ? <ChevronUp size={15} aria-hidden="true" /> : <ChevronDown size={15} aria-hidden="true" />}
        </button>
        <button
          type="button"
          onClick={() => onRemove(item.id)}
          aria-label={`Remove ${item.title}`}
          className="w-7 h-7 rounded-full text-t4 hover:text-acc hover:bg-acc-bg flex items-center justify-center shrink-0"
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pt-2.5 mx-2.5 border-t border-line flex flex-col gap-2.5 wp-fade">
          <label className="block">
            <span className="wp-label !mb-1.5 !text-[12px]">Notes</span>
            <Textarea rows={2} value={item.notes || ''} onChange={(e) => onUpdateNotes(item.id, e.target.value)} placeholder="Add performance notes..." className="!text-[13px] !resize-none" />
          </label>
          {isSong && (
            <label className="block">
              <span className="wp-label !mb-1.5 !text-[12px]">Lyrics</span>
              <Textarea rows={4} value={item.lyrics || ''} onChange={(e) => onUpdateLyrics(item.id, e.target.value)} placeholder="Paste lyrics here..." className="!text-[13px] font-mono" />
            </label>
          )}
        </div>
      )}
    </div>
  );
};
